import React from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';

interface ModuleSection {
  title: string;
  content: string[];
  illustrationText?: string;
  additionalSection?: {
    title: string;
    content: string[];
  };
  additionalSection2?: {
    title: string;
    content: string[];
  };
}

const moduleContent: ModuleSection[] = [
  {
    title: 'Rest – Understanding Sleep & Recovery',
    content: [
      '<strong>Learning Goal:</strong> Recognize the importance of rest and learn how to improve sleep quality for better health, energy, and mood.',
      '<br/>',
      '<strong>Key Topics:</strong>',
      '• Why sleep is essential for health',
      '• Understanding sleep quality (PSQI framework)',
      '• Common sleep barriers and solutions',
      '• Building better sleep habits',
      '<br/>',
      '<strong>Discussion:</strong> Sleep is your body\'s reset system. It repairs tissues, balances hormones, and clears the brain of stress buildup. Quality sleep isn\'t a luxury–it\'s maintenance for your physical and mental health.'
    ]
  },
  {
    title: 'Why Sleep Is Essential',
    content: [
      'Sleep is not just "down time"–it\'s an active process where your body performs critical maintenance and repair functions.',
      '<br/>',
      '<strong>Physical Restoration:</strong>',
      '• Repairs tissues and muscles',
      '• Strengthens immune system',
      '• Regulates hormones (including hunger and stress hormones)',
      '• Supports healthy weight management',
      '<br/>',
      '<strong>Mental Health Benefits:</strong>',
      '• Processes emotions and memories',
      '• Clears brain "waste" products',
      '• Improves focus and decision-making',
      '• Reduces risk of depression and anxiety',
      '<br/>',
      '<strong>Daily Function:</strong>',
      '• Restores energy levels',
      '• Balances mood and emotional regulation',
      '• Supports cardiovascular health',
      '• Maintains metabolic function',
      '<br/>',
      '<strong>The Mindset Shift:</strong> Sleep is not laziness–it\'s investment in your health. Every night of quality sleep makes you stronger, sharper, and more resilient.'
    ]
  },
  {
    title: 'What Makes "Good Sleep"? (Based on PSQI Framework)',
    content: [
      'The Pittsburgh Sleep Quality Index (PSQI) measures sleep quality across 7 key components. Good sleep isn\'t just about hours–it\'s about quality and consistency.',
      '<br/>',
      '<div class="module-table">' +
        '<div class="table-row header-row">' +
          '<div class="table-header pillar-cell">COMPONENT</div>' +
          '<div class="table-header means-cell">MEANING</div>' +
          '<div class="table-header matters-cell">IDEAL RANGE</div>' +
        '</div>' +
        '<div class="table-row">' +
          '<div class="table-cell pillar-cell">Sleep Quality</div>' +
          '<div class="table-cell means-cell">How rested you feel</div>' +
          '<div class="table-cell matters-cell">"Good" or "Very good"</div>' +
        '</div>' +
        '<div class="table-row">' +
          '<div class="table-cell pillar-cell">Sleep Latency</div>' +
          '<div class="table-cell means-cell">How fast you fall asleep</div>' +
          '<div class="table-cell matters-cell">&lt;30 minutes</div>' +
        '</div>' +
        '<div class="table-row">' +
          '<div class="table-cell pillar-cell">Sleep Duration</div>' +
          '<div class="table-cell means-cell">Total sleep hours</div>' +
          '<div class="table-cell matters-cell">7–9 hrs/night</div>' +
        '</div>' +
        '<div class="table-row">' +
          '<div class="table-cell pillar-cell">Sleep Efficiency</div>' +
          '<div class="table-cell means-cell">Time asleep ÷ Time in bed</div>' +
          '<div class="table-cell matters-cell">≥85%</div>' +
        '</div>' +
        '<div class="table-row">' +
          '<div class="table-cell pillar-cell">Sleep Disturbance</div>' +
          '<div class="table-cell means-cell">Night waking, pain, nightmares</div>' +
          '<div class="table-cell matters-cell">Minimal</div>' +
        '</div>' +
        '<div class="table-row">' +
          '<div class="table-cell pillar-cell">Medication Use</div>' +
          '<div class="table-cell means-cell">Sleeping pills or aids</div>' +
          '<div class="table-cell matters-cell">None or rare</div>' +
        '</div>' +
        '<div class="table-row">' +
          '<div class="table-cell pillar-cell">Daytime Energy</div>' +
          '<div class="table-cell means-cell">Sleepiness, fatigue</div>' +
          '<div class="table-cell matters-cell">Minimal</div>' +
        '</div>' +
      '</div>',
      '<br/>',
      '<strong>Key Insight:</strong> Sleep quality matters more than quantity alone. Even if you get 8 hours, poor quality sleep won\'t leave you feeling rested.'
    ]
  },
  {
    title: 'Sleep Check',
    content: [
      'Take this quick assessment of your sleep patterns. Reflect on your sleep over the past week:',
      '<br/>',
      '<strong>Sleep Onset (Falling Asleep):</strong>',
      '• Do you fall asleep within 30 minutes? (Yes/No)',
      '• What usually prevents you from falling asleep quickly?',
      '<br/>',
      '<strong>Sleep Maintenance (Staying Asleep):</strong>',
      '• Do you wake up frequently during the night? (Yes/No)',
      '• How many times do you wake up?',
      '• What causes you to wake up (noise, discomfort, etc.)?',
      '<br/>',
      '<strong>Sleep Quality (Feeling Rested):</strong>',
      '• Do you feel tired or groggy during the day? (Yes/No)',
      '• How would you rate your sleep quality (poor/fair/good/excellent)?',
      '<br/>',
      '<strong>Red Flags for Poor Sleep:</strong>',
      '• Taking more than 30 minutes to fall asleep regularly',
      '• Waking up multiple times per night',
      '• Feeling tired despite getting 7+ hours of sleep',
      '• Experiencing daytime sleepiness that affects your function',
      '<br/>',
      '<strong>If you answered "yes" to any red flags:</strong> Your body may need better sleep hygiene, not just more time in bed. Small changes can make a big difference.'
    ],
    additionalSection: {
      title: 'Sleep Diary Activity',
      content: [
        'Keep a simple sleep diary for 3 nights:',
        '• <strong>Bedtime:</strong> What time did you go to bed?',
        '• <strong>Sleep onset:</strong> How long did it take to fall asleep?',
        '• <strong>Night waking:</strong> How many times did you wake up?',
        '• <strong>Wake time:</strong> What time did you wake up?',
        '• <strong>Sleep quality:</strong> How rested did you feel (1-10)?',
        '<br/>',
        '<strong>Patterns to look for:</strong> Are there differences between weekday and weekend sleep? What factors seem to help or hurt your sleep?'
      ]
    }
  },
  {
    title: 'How to Improve Sleep Quality',
    content: [
      'Small, consistent changes to your sleep habits can dramatically improve sleep quality. Focus on these evidence-based strategies:',
      '<br/>',
      '<strong>Timing & Routine:</strong>',
      '• <strong>Consistent Bedtime:</strong> Go to bed and wake up at the same time daily, even on weekends',
      '• <strong>Wind-Down Routine:</strong> Create a relaxing pre-bed ritual (reading, light stretching, deep breathing)',
      '• <strong>Morning Light:</strong> Get natural sunlight within the first hour of waking to regulate your circadian rhythm',
      '<br/>',
      '<strong>Environment:</strong>',
      '• <strong>Cool, Dark Room:</strong> Keep bedroom temperature around 65°F (18°C) and use blackout curtains',
      '• <strong>Comfortable Setup:</strong> Invest in a good mattress, pillows, and breathable bedding',
      '• <strong>Quiet Space:</strong> Use white noise machines or earplugs if needed',
      '<br/>',
      '<strong>Habits to Avoid:</strong>',
      '• <strong>Screen-Free Hour:</strong> No phones, tablets, or computers 1 hour before bed (blue light disrupts melatonin)',
      '• <strong>Caffeine Cut-Off:</strong> Avoid caffeine after 3 PM (it stays in your system for 6-8 hours)',
      '• <strong>Late Eating:</strong> Finish eating 2-3 hours before bed to avoid digestion interference',
      '<br/>',
      '<strong>Daytime Support:</strong>',
      '• <strong>Regular Exercise:</strong> Physical activity during the day promotes better sleep',
      '• <strong>Stress Management:</strong> Practice relaxation techniques if racing thoughts keep you awake',
      '• <strong>Nap Wisely:</strong> Keep naps under 30 minutes and before 3 PM if needed'
    ],
    additionalSection: {
      title: 'Reflection',
      content: [
        '<strong>Journal Prompt:</strong> "My body feels most rested when I…"',
        '"One change I can make to improve my sleep is…"',
        '<br/>',
        '<strong>Common Sleep Myths to Avoid:</strong>',
        '• "I can catch up on sleep on weekends" (inconsistent sleep disrupts your rhythm)',
        '• "Alcohol helps me sleep better" (it disrupts sleep cycles and reduces quality)',
        '• "Older adults need less sleep" (sleep needs remain 7-9 hours throughout adulthood)',
        '<br/>',
        '<strong>Takeaway:</strong> Quality sleep is a skill you can develop. Start with one or two changes and build from there. Better sleep leads to better health, mood, and energy.'
      ]
    }
  },
  {
    title: 'Ready to Test Your Knowledge?',
    content: [
      'You\'ve completed Module 2: Rest – Understanding Sleep & Recovery! Now, let\'s reinforce your learning with a quick quiz.'
    ],
    additionalSection: {
      title: 'Next Steps',
      content: [
        'Click "Take the Quiz" below to test your knowledge about sleep quality and recovery.',
        'The quiz will help you review key concepts about building healthy sleep habits.'
      ]
    }
  }
];

const PhysicalModule2Page: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress, isCompleted } = useModuleProgress('physicalM2');
  const totalSteps = moduleContent.length;

  const safeCurrentStep = Math.max(0, Math.min(currentStep, totalSteps - 1));
  const currentModule = moduleContent[safeCurrentStep];
  const progress = ((safeCurrentStep + 1) / totalSteps) * 100;

  const handleNext = async () => {
    if (currentStep < totalSteps - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      await saveProgress(nextStep, false);
    } else {
      await saveProgress(currentStep, true);
      navigate('/panel/learn/physical-module-2/quiz');
    }
  };

  const handleBack = async () => {
    if (currentStep === 0) {
      navigate('/panel/learn/physical-resources');
      return;
    }
    const prevStep = currentStep - 1;
    setCurrentStep(prevStep);
    await saveProgress(prevStep, isCompleted);
  };

  const showCompletionMessage = currentStep === totalSteps - 1;

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading your progress...</p>
      </div>
    );
  }

  if (!currentModule) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Error loading module content. Please try refreshing the page.</p>
      </div>
    );
  }

  return (
    <div className="module-form">
      <div className="module-container">
        <div
          className="module-left physical-panel"
        >
          <div className="module-header">
            <h1>Module 2</h1>
            <p className="module-subtitle">Rest – Understanding Sleep & Recovery</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Recognize the importance of rest and learn how to improve sleep quality for better health, energy, and mood.
            </p>
          </div>
        </div>

        <div className="module-right">
          <div className="module-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: progress + '%'}}></div>
              </div>
              <span className="progress-text">
                Page {currentStep + 1} of {totalSteps}
              </span>
            </div>

            <div className="module-section">
              <h2 className="module-title">
                {currentModule.title}
              </h2>
              <div className="content-section">
                {currentModule.content.map((paragraph, index) => (
                  <p key={index} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                ))}
              </div>
            </div>

            {currentModule.additionalSection && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection.content.map((paragraph: string, index: number) => (
                    <p key={'additional-' + index} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
            {currentModule.additionalSection2 && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection2.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection2.content.map((paragraph: string, index: number) => (
                    <p key={'additional2-' + index} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={handleBack}
              disabled={false}
            >
              {currentStep === 0 ? 'Exit Module' : 'Previous'}
            </button>

            {showCompletionMessage ? (
              <button
                className="btn-primary"
                onClick={() => navigate('/panel/learn/physical-module-2/quiz')}
              >
                Take the Quiz
              </button>
            ) : (
              <button
                className="btn-primary"
                onClick={handleNext}
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhysicalModule2Page;

